import { useEffect, useRef, useCallback } from "react";
import { AppState, Platform, DeviceEventEmitter } from "react-native";
import { Image as ExpoImage } from "expo-image";

type MemoryWarningCallback = () => void;

let memoryPressureLevel = 0;

/**
 * Clears decoded-bitmap caches for both iOS (SDWebImage) and Android (Glide).
 *
 * iOS: `clearMemoryCache` removes decoded bitmaps from SDWebImage's NSCache.
 * It does NOT release CGImage refs held by live UIImageViews or IOSurface
 * textures committed to CoreAnimation's render server.
 * We also clear the disk LRU on critical pressure to prevent SDWebImage from
 * re-decoding originals on next access.
 *
 * Android: `clearMemoryCache` flushes Glide's LruResourceCache (decoded Bitmap
 * objects). This is the primary native-heap consumer on Android — full-resolution
 * bitmaps accumulate in Glide's memory cache across pages of search results and
 * are not released until this is called. `clearDiskCache` is cheap on Android
 * since Glide's DiskLruCache stores compressed bytes, not decoded bitmaps.
 */
function aggressiveCacheClear(critical: boolean) {
  ExpoImage.clearMemoryCache().catch(() => {});

  if (critical) {
    ExpoImage.clearDiskCache().catch(() => {});
  }
}

/**
 * Listens for system memory pressure signals and clears image caches.
 *
 * iOS: React Native surfaces "memoryWarning" on AppState (up to 3 warnings
 * before Jetsam terminates the app). We escalate to disk-cache eviction on
 * repeated warnings.
 *
 * Android: React Native does not expose a direct "memoryWarning" AppState
 * event. Instead we treat "background" transitions as a cache-clearing
 * opportunity — backgrounded apps are the first candidates for memory
 * reclamation by Android's LMK (Low Memory Killer), so clearing caches
 * proactively reduces the chance the process is killed. We also emit
 * MEMORY_PRESSURE so list screens can evict stale query cache entries.
 *
 * This hook:
 * 1. Clears expo-image in-memory bitmap cache on first warning / background
 * 2. Clears BOTH memory AND disk caches on repeated / critical pressure
 * 3. Emits "MEMORY_PRESSURE" so consumers (search result screen) can evict
 *    orphaned React Query cache entries
 */
export function useMemoryWarningHandler(onPressure?: MemoryWarningCallback) {
  const callbackRef = useRef(onPressure);
  callbackRef.current = onPressure;

  const handleMemoryWarning = useCallback(() => {
    memoryPressureLevel++;
    const isCritical = memoryPressureLevel >= 2;

    aggressiveCacheClear(isCritical);
    DeviceEventEmitter.emit("MEMORY_PRESSURE", { critical: isCritical });
    callbackRef.current?.();
  }, []);

  const handleBackground = useCallback(() => {
    memoryPressureLevel = 0;
    aggressiveCacheClear(false);
  }, []);

  useEffect(() => {
    const subs: Array<{ remove: () => void }> = [];

    if (Platform.OS === "ios") {
      subs.push(
        AppState.addEventListener("memoryWarning", handleMemoryWarning)
      );
    }

    let prevState = AppState.currentState;
    subs.push(
      AppState.addEventListener("change", (nextState) => {
        if (prevState === "active" && nextState !== "active") {
          handleBackground();
          if (Platform.OS === "android") {
            DeviceEventEmitter.emit("MEMORY_PRESSURE", { critical: false });
            callbackRef.current?.();
          }
        }
        if (nextState === "active") {
          memoryPressureLevel = 0;
        }
        prevState = nextState;
      })
    );

    return () => {
      subs.forEach((s) => s.remove());
    };
  }, [handleMemoryWarning, handleBackground]);
}

/**
 * Subscribes to the MEMORY_PRESSURE event emitted by useMemoryWarningHandler.
 * Use in list screens to pause image loading or reduce draw distance.
 * Works on both iOS (memoryWarning events) and Android (background transitions).
 */
export function useOnMemoryPressure(callback: () => void) {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    const sub = DeviceEventEmitter.addListener("MEMORY_PRESSURE", () => {
      callbackRef.current();
    });

    return () => sub.remove();
  }, []);
}

/**
 * Clears expo-image memory cache. Works on both iOS and Android.
 * Exported for use in navigation listeners and screen lifecycle callbacks
 * outside of React component context.
 */
export function clearImageMemoryCache() {
  ExpoImage.clearMemoryCache().catch(() => {});
}
